import React, { useState, useEffect } from 'react';
import styles from './jiraSettings.module.css';
import { get } from '../../utils/fetch';
import { Form, Row, Col, Button, Select, Switch } from 'antd';
import { message } from 'antd';
import { ConnectPrompt } from './ConnectPrompt';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          team: OptionType;
          channel: OptionType;
          new_tasks: boolean;
          status_updates: boolean;
        };
      }
    | undefined;
}

const MicrosoftTeamsSettings: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();

  const { integration, organizationId, projectId } = props;
  const [selectedTeam, setSelectedTeam] = useState(integration?.settings?.team);
  const [selectedChannel, setSelectedChannel] = useState(
    integration?.settings?.channel
  );
  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks.toString() === 'true'
  );
  const [syncStatus, setSyncStatus] = useState(
    integration?.settings?.status_updates.toString() === 'true'
  );
  const [teamOptions, setTeamOptions] = useState<OptionType[]>([]);
  const [channelOptions, setChannelOptions] = useState<OptionType[]>([]);

  const getChannelOptions = async (team: string) => {
    if (!integration || !integration?.enabled) return;
    try {
      let results = await get(
        `/organizations/${organizationId}/integrations/teams_channels?id=${integration.id}&team_id=${team}`
      );
      setChannelOptions(results);
    } catch (error) {
      message.error('Something went wrong, please try again.');
    }
  };

  const {
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Team',
    onConnectedStateLoaded: results => {
      setSelectedTeam(results.settings.team);
      setSelectedChannel(results.settings.channel);
      setAutoPost(results.settings.new_tasks.toString() === 'true');
      setSyncStatus(results.settings.status_updates.toString() === 'true');
    }
  });

  const handleTeamSelect = (
    value: string | undefined,
    option: OptionType | OptionType[]
  ) => {
    setSelectedTeam(Array.isArray(option) ? option[0] : option);
    setSelectedChannel(undefined);
    form.setFieldValue('channel', undefined);
    setChannelOptions([]);
    if (value) getChannelOptions(value);
  };

  const handleChannelSelect = (
    _value: string | undefined,
    option: OptionType | OptionType[]
  ) => {
    setSelectedChannel(Array.isArray(option) ? option[0] : option);
  };

  const getTeamOptions = async () => {
    if (!integration || !integration?.enabled) return;
    try {
      let results = await get(
        `/organizations/${organizationId}/integrations/teams_teams?id=${integration.id}`
      );
      setTeamOptions(results);
    } catch (error) {
      message.error('Something went wrong, please try again.');
    }
  };

  useEffect(() => {
    getConnectedState({
      errorMessage: 'Something went wrong, please try again.'
    });
    getTeamOptions();
    if (selectedTeam) {
      getChannelOptions(selectedTeam.value);
    }
  }, []);

  const renderIntegrationSettings = () => {
    return (
      <div>
        <Form
          form={form}
          layout="horizontal"
          autoComplete="off"
          requiredMark={false}
          onFinish={() =>
            saveSettings(
              {
                team: selectedTeam,
                channel: selectedChannel,
                new_tasks: autoPost,
                status_updates: syncStatus
              },
              {
                successMessage: 'Settings saved',
                errorMessage: 'Something went wrong, please try again.'
              }
            )
          }
        >
          <div className={styles.jiraRequired}>
            <div className={styles.requiredBubble}>REQUIRED</div>
            <h3>Connect your Microsoft Teams channel</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="teams"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Microsoft Team
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[{ required: true, message: 'Please select a team.' }]}
                  initialValue={selectedTeam?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a team"
                    optionFilterProp="label"
                    options={teamOptions}
                    onChange={handleTeamSelect}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="channel"
                  label={
                    <span className={styles.requiredLabel}>
                      <span>*</span>Microsoft Teams Channel
                    </span>
                  }
                  className={styles.jiraProject}
                  rules={[
                    { required: true, message: 'Please select a channel.' }
                  ]}
                  initialValue={selectedChannel?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a channel"
                    optionFilterProp="label"
                    options={channelOptions}
                    onChange={handleChannelSelect}
                    disabled={!selectedTeam}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className={styles.jiraWorkflows}>
            <h3>Choose your desired workflow</h3>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item label="Automatically post new tasks to Microsoft Teams">
                  <Switch checked={autoPost} onChange={setAutoPost} />
                </Form.Item>
              </Col>
            </Row>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item label="Sync status updates to Microsoft Teams">
                  <Switch checked={syncStatus} onChange={setSyncStatus} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Row
            className={styles.jiraEditRow}
            justify="space-between"
            align="middle"
          >
            <Col>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  shape="round"
                  htmlType="submit"
                  className={styles.save}
                >
                  Save
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Button
                type="text"
                shape="round"
                onClick={() =>
                  disconnect({
                    reloadAfter: true,
                    successMessage: 'Integration disconnected',
                    errorMessage: 'Something went wrong, please try again.'
                  })
                }
                className={styles.disconnectButton}
                danger
              >
                Disconnect
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {isEnabled ? (
        renderIntegrationSettings()
      ) : (
        <ConnectPrompt
          integrationName="microsoftTeams"
          projectId={projectId}
          organizationId={organizationId}
          type="Team"
          display="Microsoft Teams"
          provider="microsoft_graph"
        />
      )}
    </div>
  );
};

export default (props: Props) => <MicrosoftTeamsSettings {...props} />;
